import { existsSync } from 'node:fs';
import { resolve } from 'node:path';

/**
 * Loads `.env` into `process.env`, before anything reads it.
 *
 * **Why this file exists.** Nothing in the API ever loaded `.env`. It worked
 * anyway in development because importing `@prisma/client` quietly calls dotenv
 * for its own `DATABASE_URL` lookup, and every other variable came along for the
 * ride. That side effect does **not** survive webpack bundling: the built
 * `dist/api/main.js` contains no dotenv and no `.env` handling at all, so the
 * served API saw none of them. The database still worked — Prisma's query engine
 * reads `.env` itself, separately — which is what made the gap so hard to spot:
 * the app booted, queried fine, and only features reading `process.env` directly
 * behaved as if unconfigured. `PLATFORM_R2_*` was the first to notice.
 *
 * **Why it is a separate module, imported first.** Imports are hoisted and
 * evaluated before any statement in the importing file, and several modules read
 * `process.env` at module scope (`STORAGE_WARN_PERCENT`, `LOW_STOCK_THRESHOLD`
 * and friends). A `loadEnvFile()` call at the top of `main.ts` would still run
 * after those, and they would capture undefined. The first import wins, so this
 * has to be it.
 *
 * **Production is unaffected.** PM2 supplies the environment directly from the
 * deploy workflow and there is no `.env` on the server, so this no-ops there.
 * Values already present are never overwritten — Node's loader leaves existing
 * variables alone — so a real environment always beats a stray file.
 */
const envPath = resolve(process.cwd(), '.env');

if (existsSync(envPath)) {
  // Added in Node 20.12; the workspace targets 22. Guarded rather than assumed
  // because an env-loading failure at boot is a confusing way to lose a server.
  if (typeof process.loadEnvFile === 'function') {
    process.loadEnvFile(envPath);
  } else {
    console.warn(
      `[load-env] ${envPath} exists but process.loadEnvFile is unavailable on ${process.version} — ` +
        'variables from it will be missing. Node 20.12+ is required.'
    );
  }
}
